import { useEffect, useRef, useState } from 'react'

import { logDebug } from '@core/logging'
import type { Channel, Socket } from '@core/phoenix/core'
import type {
  PhoenixPushEventMap,
  PhoenixServerEventMap,
} from '@core/phoenix/core/types'

export interface UseChannelProps<
  PushEventMap extends PhoenixPushEventMap = PhoenixPushEventMap,
  ServerEventMap extends PhoenixServerEventMap = PhoenixServerEventMap,
> {
  topic: string
  socket: Socket | undefined
  payload: PushEventMap['phx_join']['request_payload']
  shouldJoin: boolean
  handleMessages?: (
    channel: Channel<PushEventMap, ServerEventMap>,
  ) => () => void
  onJoin?: () => void
  onJoinSuccess?: (
    response: NonNullable<PushEventMap['phx_join']['success_payload']>,
  ) => void
  onJoinError?: (
    response: NonNullable<PushEventMap['phx_join']['error_payload']>,
  ) => void
  onClose?: (manual?: boolean) => void
}

/**
 * Hook to create a new channel instance and joins the topic.
 * @returns The channel instance after it triggers the join event.
 */
export function useChannel<
  PushEventMap extends PhoenixPushEventMap = PhoenixPushEventMap,
  ServerEventMap extends PhoenixServerEventMap = PhoenixServerEventMap,
>({
  topic,
  socket,
  payload,
  shouldJoin,
  handleMessages,
  onClose,
  onJoin,
  onJoinError,
  onJoinSuccess,
}: UseChannelProps<PushEventMap, ServerEventMap>):
  Channel<PushEventMap, ServerEventMap> | undefined {
  const [channel, setChannel] = useState<
    Channel<PushEventMap, ServerEventMap> | undefined
  >(undefined)
  const [registeredHandlers, setRegisteredHandlers] = useState(false)
  const [triggeredJoin, setTriggeredJoin] = useState(false)
  // Use refs to keep the callbacks up to date and not re-trigger the join
  // channel effect when they change.
  const onJoinRef = useRef(onJoin)
  const onJoinErrorRef = useRef(onJoinError)
  const onJoinSuccessRef = useRef(onJoinSuccess)
  const onCloseRef = useRef(onClose)

  useEffect(() => {
    onJoinRef.current = onJoin
    onJoinErrorRef.current = onJoinError
    onJoinSuccessRef.current = onJoinSuccess
    onCloseRef.current = onClose
  }, [onClose, onJoin, onJoinError, onJoinSuccess])

  useEffect(() => {
    if (shouldJoin && socket && topic) {
      const newChannel = socket.channel<PushEventMap, ServerEventMap>(
        topic,
        payload,
      )
      setChannel(newChannel)

      return () => {
        setChannel(undefined)
        setRegisteredHandlers(false)
        setTriggeredJoin(false)
      }
    }
  }, [payload, shouldJoin, socket, topic])

  useEffect(() => {
    if (channel) {
      let unsubscribe: (() => void) | undefined
      if (handleMessages) {
        unsubscribe = handleMessages(channel)
      }
      setRegisteredHandlers(true)
      return () => {
        unsubscribe?.()
      }
    }
  }, [channel, handleMessages])

  useEffect(() => {
    if (channel && registeredHandlers && shouldJoin) {
      setTriggeredJoin(true)
      onJoinRef.current?.()
      channel
        .join()
        .receive('ok', (response) => {
          logDebug(`Joined channel: ${channel.topic}`)
          onJoinSuccessRef.current?.(response)
        })
        .receive('error', (response) => {
          logDebug(
            `Joined channel failed: ${channel.topic} - ${JSON.stringify(
              response,
            )}`,
          )
          onJoinErrorRef.current?.(response)
        })
        .receive('timeout', () => {
          logDebug(`Joined channel timeout: ${channel.topic}`)
          onJoinErrorRef.current?.({ reason: 'REASON_TIMEOUT' })
        })

      channel.onClose(() => {
        logDebug(`Channel closed: ${channel.topic}`)
        onCloseRef.current?.()
      })

      return () => {
        channel.leave()
        onCloseRef.current?.(true)
      }
    }
  }, [channel, registeredHandlers, shouldJoin])

  return triggeredJoin ? channel : undefined
}
